import {css, cx} from "@panda/css";

import {Text} from "@pg-design/text-module";
import {getAnalyticsSessionId} from "@pg-mono/algolytics";
import {rpAppPath} from "@pg-mono/rp-routes";

import {useCurrentRoute} from "../../../hooks/use_current_route";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {ISelectedRegion} from "../../../offer/list/actions/offer_list_selected_data_actions";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {isRtkqError} from "../../../store/utils/is_rtkq_error";
import {usePostSemanticSearchMutation} from "../../api/post_semantic_search_api";
import {postSemanticSearchCacheKey} from "../../constants/post_semantic_search_cache_key";
import {useSemanticSearchLocalStorage} from "../../hooks/use_semantic_search_local_storage";
import {selectSemanticSearchHasConversation} from "../../state/select_semantic_search_has_conversation";
import {GetSemanticSearchStoreResponseDto} from "../../types/GetSemanticSearchStoreResponseDto";
import {PostSemanticSearchDataItemDto} from "../../types/PostSemanticSearchDataItemDto";
import {SemanticSearchFormValues} from "../../types/SemanticSearchFormValues";
import {SemanticSearchQueryForm} from "./SemanticSearchQueryForm";

interface IProps {
    hasStickySidebar: boolean;
}

const SEMANTIC_SEARCH_ERROR_MESSAGES: Record<number, string> = {
    403: "Nie masz dostępu do tej funkcjonalności. Skontaktuj się z administratorem.",
    422: "Zapytanie jest nieprawidłowe. Zmień treść i spróbuj ponownie.",
    500: "Wystąpił błąd wewnętrzny. Spróbuj ponownie później.",
    502: "Wyszukiwarka jest tymczasowo niedostępna. Spróbuj ponownie za chwilę."
};

function getSemanticSearchErrorMessage(status: number): string {
    return SEMANTIC_SEARCH_ERROR_MESSAGES[status] ?? "Wystąpił błąd. Spróbuj ponownie.";
}

export function SemanticSearchQuery(props: IProps) {
    const {hasStickySidebar} = props;

    const isSemanticSearchOpen = useAppSelector((state) => state.semanticSearchModal.isSemanticSearchOpen);

    const dispatch = useAppDispatch();
    const {pathname} = useCurrentRoute();
    const hasConversation = useAppSelector(selectSemanticSearchHasConversation);

    const [postSemanticSearch, {isLoading: isPostSemanticSearchLoading}] = usePostSemanticSearchMutation({fixedCacheKey: postSemanticSearchCacheKey});
    const {history: semanticSearchHistory, isLoading: isSemanticSearchHistoryLoading, addMessage, clearHistory} = useSemanticSearchLocalStorage();

    const selectedRegions = useAppSelector((state) => state.offerList.selectedRegions as ISelectedRegion[]);

    // Display clear history button only on /szukaj page
    const isSearchPage = pathname.includes(rpAppPath.semanticSearch.base);

    const regions = selectedRegions.map((region) => region.id);

    const handleSubmit = async (data: SemanticSearchFormValues) => {
        const analyticsSessionId = getAnalyticsSessionId();

        const semanticSearchResultsHistory = convertToPostSemanticSearchResultsHistory(semanticSearchHistory);

        addMessage({
            type: "USER_INPUT",
            text: data.userQuery
        });

        const semanticSearchResult = await postSemanticSearch({
            // Casting because we should have string, TODO: Consider sending error to bugsnag if missing
            analytic_session_id: analyticsSessionId as string,
            data: [
                ...semanticSearchResultsHistory,
                {
                    type: "USER_INPUT",
                    text: data.userQuery,
                    results: []
                }
            ],
            regions
        });

        if (semanticSearchResult.error && isRtkqError(semanticSearchResult.error)) {
            const status = typeof semanticSearchResult.error.status === "number" ? semanticSearchResult.error.status : 500;

            dispatch(addFailureNotification(getSemanticSearchErrorMessage(status)));

            if (status === 403) {
                window.location.reload();
            }

            return;
        }

        if (semanticSearchResult.data) {
            addMessage({
                type: "API_RESPONSE",
                text: semanticSearchResult.data.response_text,
                results: semanticSearchResult.data.properties,
                offer_signup: semanticSearchResult.data.offer_signup
            });
        }
    };

    const handleClearHistory = () => {
        clearHistory();
    };

    const isLoading = isPostSemanticSearchLoading || isSemanticSearchHistoryLoading;

    const shouldBeSticky = hasConversation || isSemanticSearchOpen || isSearchPage;

    return (
        <div
            className={cx(semanticSearchQueryWrapper, hasStickySidebar && semanticSearchQueryWrapperStyling)}
            style={{position: shouldBeSticky ? "sticky" : "absolute"}}
        >
            <div className={cx(semanticSearchQueryContent, hasStickySidebar && semanticSearchQueryContentSticky)}>
                <div className={cx(semanticSearchFormWrap, hasStickySidebar && semanticSearchFormWrapSticky)}>
                    {semanticSearchHistory && semanticSearchHistory.length > 0 && isSearchPage && (
                        <div className={clearHistoryLinkWrapper}>
                            <button type="button" onClick={handleClearHistory} className={clearHistoryLink}>
                                <Text variant="info_txt_1" className={clearHistoryLinkText}>
                                    Zacznij od nowa
                                </Text>
                            </button>
                        </div>
                    )}

                    <SemanticSearchQueryForm
                        handleSubmit={handleSubmit}
                        disabled={isLoading}
                        hasConversation={hasConversation}
                        hasStickySidebar={hasStickySidebar}
                    />
                </div>
            </div>
        </div>
    );
}

// Utils
const MAX_CONVERSATION_HISTORY = 20;

function convertToPostSemanticSearchResultsHistory(semanticSearchHistory: GetSemanticSearchStoreResponseDto | undefined): PostSemanticSearchDataItemDto[] {
    if (!semanticSearchHistory || semanticSearchHistory.length === 0) return [];

    const recentHistory = semanticSearchHistory.slice(-MAX_CONVERSATION_HISTORY);

    return recentHistory.map((item) => ({
        type: item.type,
        text: item.text,
        results: item.results?.length ? item.results.map((group) => group.map((property) => property.id)) : []
    }));
}

// Styles
const semanticSearchQueryWrapper = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    p: 1.5,
    transition: "all 0.3s ease-in-out",
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: 10
});

const semanticSearchQueryWrapperStyling = css({
    p: 0,
    background: "var(--colors-white)"
});

const semanticSearchQueryContent = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    width: "100%",
    maxWidth: "744px"
});

const semanticSearchQueryContentSticky = css({
    py: 2,
    px: 1.5
});

const semanticSearchFormWrap = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    transition: "all 0.3s ease-in-out"
});

const semanticSearchFormWrapSticky = css({
    p: 0,
    background: "var(--colors-white)",
    borderRadius: 2
});

const clearHistoryLinkWrapper = css({
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginBottom: 1
});

const clearHistoryLink = css({
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer"
});

const clearHistoryLinkText = css({
    textDecoration: "underline",
    color: "var(--colors-gray-700)",

    _hover: {
        color: "var(--colors-gray-900)"
    }
});
import {css, cx} from "@panda/css";

import {Text} from "@pg-design/text-module";
import {getAnalyticsSessionId} from "@pg-mono/algolytics";
import {rpAppPath} from "@pg-mono/rp-routes";

import {useCurrentRoute} from "../../../hooks/use_current_route";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {ISelectedRegion} from "../../../offer/list/actions/offer_list_selected_data_actions";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {isRtkqError} from "../../../store/utils/is_rtkq_error";
import {usePostSemanticSearchMutation} from "../../api/post_semantic_search_api";
import {postSemanticSearchCacheKey} from "../../constants/post_semantic_search_cache_key";
import {useSemanticSearchLocalStorage} from "../../hooks/use_semantic_search_local_storage";
import {selectSemanticSearchHasConversation} from "../../state/select_semantic_search_has_conversation";
import {GetSemanticSearchStoreResponseDto} from "../../types/GetSemanticSearchStoreResponseDto";
import {PostSemanticSearchDataItemDto} from "../../types/PostSemanticSearchDataItemDto";
import {SemanticSearchFormValues} from "../../types/SemanticSearchFormValues";
import {SemanticSearchQueryForm} from "./SemanticSearchQueryForm";

interface IProps {
    hasStickySidebar: boolean;
}

const SEMANTIC_SEARCH_ERROR_MESSAGES: Record<number, string> = {
    403: "Nie masz dostępu do tej funkcjonalności. Skontaktuj się z administratorem.",
    422: "Zapytanie jest nieprawidłowe. Zmień treść i spróbuj ponownie.",
    500: "Wystąpił błąd wewnętrzny. Spróbuj ponownie później.",
    502: "Wyszukiwarka jest tymczasowo niedostępna. Spróbuj ponownie za chwilę."
};

function getSemanticSearchErrorMessage(status: number): string {
    return SEMANTIC_SEARCH_ERROR_MESSAGES[status] ?? "Wystąpił błąd. Spróbuj ponownie.";
}

export function SemanticSearchQuery(props: IProps) {
    const {hasStickySidebar} = props;

    const isSemanticSearchOpen = useAppSelector((state) => state.semanticSearchModal.isSemanticSearchOpen);

    const dispatch = useAppDispatch();
    const {pathname} = useCurrentRoute();
    const hasConversation = useAppSelector(selectSemanticSearchHasConversation);

    const [postSemanticSearch, {isLoading: isPostSemanticSearchLoading}] = usePostSemanticSearchMutation({fixedCacheKey: postSemanticSearchCacheKey});
    const {history: semanticSearchHistory, isLoading: isSemanticSearchHistoryLoading, addMessage, clearHistory} = useSemanticSearchLocalStorage();

    const selectedRegions = useAppSelector((state) => state.offerList.selectedRegions as ISelectedRegion[]);

    // Display clear history button only on /szukaj page
    const isSearchPage = pathname.includes(rpAppPath.semanticSearch.base);

    const regions = selectedRegions.map((region) => region.id);

    const handleSubmit = async (data: SemanticSearchFormValues) => {
        const analyticsSessionId = getAnalyticsSessionId();

        const semanticSearchResultsHistory = convertToPostSemanticSearchResultsHistory(semanticSearchHistory);

        addMessage({
            type: "USER_INPUT",
            text: data.userQuery
        });

        const semanticSearchResult = await postSemanticSearch({
            // Casting because we should have string, TODO: Consider sending error to bugsnag if missing
            analytic_session_id: analyticsSessionId as string,
            data: [
                ...semanticSearchResultsHistory,
                {
                    type: "USER_INPUT",
                    text: data.userQuery,
                    results: []
                }
            ],
            regions
        });

        if (semanticSearchResult.error && isRtkqError(semanticSearchResult.error)) {
            const status = typeof semanticSearchResult.error.status === "number" ? semanticSearchResult.error.status : 500;

            dispatch(addFailureNotification(getSemanticSearchErrorMessage(status)));

            if (status === 403) {
                window.location.reload();
            }

            return;
        }

        if (semanticSearchResult.data) {
            addMessage({
                type: "API_RESPONSE",
                text: semanticSearchResult.data.response_text,
                results: semanticSearchResult.data.properties,
                offer_signup: semanticSearchResult.data.offer_signup
            });
        }
    };

    const handleClearHistory = () => {
        clearHistory();
    };

    const isLoading = isPostSemanticSearchLoading || isSemanticSearchHistoryLoading;

    const shouldBeSticky = hasConversation || isSemanticSearchOpen || isSearchPage;

    return (
        <div
            className={cx(semanticSearchQueryWrapper, hasStickySidebar && semanticSearchQueryWrapperStyling)}
            style={{position: shouldBeSticky ? "sticky" : "absolute"}}
        >
            <div className={cx(semanticSearchQueryContent, hasStickySidebar && semanticSearchQueryContentSticky)}>
                <div className={cx(semanticSearchFormWrap, hasStickySidebar && semanticSearchFormWrapSticky)}>
                    {semanticSearchHistory && semanticSearchHistory.length > 0 && isSearchPage && (
                        <div className={clearHistoryLinkWrapper}>
                            <button type="button" onClick={handleClearHistory} className={clearHistoryLink}>
                                <Text variant="info_txt_1" className={clearHistoryLinkText}>
                                    Zacznij od nowa
                                </Text>
                            </button>
                        </div>
                    )}

                    <SemanticSearchQueryForm
                        handleSubmit={handleSubmit}
                        disabled={isLoading}
                        hasConversation={hasConversation}
                        hasStickySidebar={hasStickySidebar}
                    />
                </div>
            </div>
        </div>
    );
}

// Utils
const MAX_CONVERSATION_HISTORY = 20;

function convertToPostSemanticSearchResultsHistory(semanticSearchHistory: GetSemanticSearchStoreResponseDto | undefined): PostSemanticSearchDataItemDto[] {
    if (!semanticSearchHistory || semanticSearchHistory.length === 0) return [];

    const recentHistory = semanticSearchHistory.slice(-MAX_CONVERSATION_HISTORY);

    return recentHistory.map((item) => ({
        type: item.type,
        text: item.text,
        results: item.results?.length ? item.results.map((group) => group.map((property) => property.id)) : []
    }));
}

// Styles
const semanticSearchQueryWrapper = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    p: 1.5,
    transition: "all 0.3s ease-in-out",
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: 10
});

const semanticSearchQueryWrapperStyling = css({
    p: 0,
    background: "var(--colors-white)"
});

const semanticSearchQueryContent = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    width: "100%",
    maxWidth: "744px"
});

const semanticSearchQueryContentSticky = css({
    py: 2,
    px: 1.5
});

const semanticSearchFormWrap = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    transition: "all 0.3s ease-in-out"
});

const semanticSearchFormWrapSticky = css({
    p: 0,
    background: "var(--colors-white)",
    borderRadius: 2
});

const clearHistoryLinkWrapper = css({
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginBottom: 1
});

const clearHistoryLink = css({
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer"
});

const clearHistoryLinkText = css({
    textDecoration: "underline",
    color: "var(--colors-gray-700)",

    _hover: {
        color: "var(--colors-gray-900)"
    }
});
